import { useQuery } from '@tanstack/react-query';
import { ccEventsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  CcCommandMatrixParams,
  CcEventsByNameParams,
  CcEventsQueryParams,
  CcEventsTimeseriesParams,
} from '../types/api.js';

/**
 * AI 規定コマンド利用状況マトリクス (人 × 工程)。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events command matrix (member axis) fetch hook
 * @graph-connects /cc-events/command-matrix/members [calls] 人 × 工程マトリクス取得
 */
export function useCcCommandMatrixMembers(params: CcCommandMatrixParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.ccEvents.commandMatrixMembers(params),
    queryFn: () => ccEventsApi.getCommandMatrixMembers(params),
    enabled,
  });
}

/**
 * AI 規定コマンド利用状況マトリクス (PJ × 工程)。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events command matrix (project axis) fetch hook
 * @graph-connects /cc-events/command-matrix/projects [calls] PJ × 工程マトリクス取得
 */
export function useCcCommandMatrixProjects(params: CcCommandMatrixParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.ccEvents.commandMatrixProjects(params),
    queryFn: () => ccEventsApi.getCommandMatrixProjects(params),
    enabled,
  });
}

/**
 * Claude Code 実行イベントサマリ。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events summary fetch hook
 * @graph-connects /cc-events/summary [calls] cc-events サマリ取得
 */
export function useCcEventsSummary(params: CcEventsQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccEvents.summary(params),
    queryFn: () => ccEventsApi.getSummary(params),
  });
}

/**
 * Claude Code 実行イベントの event_name 別ランキング。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events by name fetch hook
 * @graph-connects /cc-events/by-name [calls] cc-events event_name 別ランキング取得
 */
export function useCcEventsByName(params: CcEventsByNameParams) {
  return useQuery({
    queryKey: queryKeys.ccEvents.byName(params),
    queryFn: () => ccEventsApi.getByName(params),
  });
}

/**
 * Claude Code 実行イベントの user 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events by user fetch hook
 * @graph-connects /cc-events/by-user [calls] cc-events user 別 breakdown 取得
 */
export function useCcEventsByUser(params: CcEventsQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccEvents.byUser(params),
    queryFn: () => ccEventsApi.getByUser(params),
  });
}

/**
 * Claude Code 実行イベントの Backlog チケット別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events by project fetch hook
 * @graph-connects /cc-events/by-project [calls] cc-events Backlog チケット別 breakdown 取得
 */
export function useCcEventsByProject(params: CcEventsQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccEvents.byProject(params),
    queryFn: () => ccEventsApi.getByProject(params),
  });
}

/**
 * Claude Code 実行イベントの時系列推移。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events timeseries fetch hook
 * @graph-connects /cc-events/timeseries [calls] cc-events 時系列取得
 */
export function useCcEventsTimeseries(params: CcEventsTimeseriesParams) {
  return useQuery({
    queryKey: queryKeys.ccEvents.timeseries(params),
    queryFn: () => ccEventsApi.getTimeseries(params),
  });
}
